// src/services/screeningVisit.js
//
// The study's SCREENING VISIT checklist — "Needs informed consent", "Needs
// spirometry", "Needs 12-lead ECG" — derived from the protocol's Schedule of
// Assessments and confirmed by an admin on the study page. Coordinators then
// see it on any patient marked eligible for the study.
//
// Pure: data in, data out, no Firebase. deriveScreeningItems() reads the SoA
// extraction the Protocol -> SoA pipeline already produces (protocolTypes.js
// documents the shape) and lists every assessment with a non-blank mark in the
// screening column(s). Nothing is summarised or renamed by an AI here — the
// items ARE the SoA's row names, so the admin is ticking what the protocol
// says, not what a model thought it said.
//
// Stored on the study document as:
//
//   screeningVisit: {
//     status: "confirmed",
//     items: [{ id, label, assessmentName, source: "soa" | "manual", footnotes: [] }],
//     protocolId, visitLabel, visitIds, guessedVisit,
//     confirmedAt, confirmedBy,
//   }
//
// Only SELECTED items are stored — the checklist is what the admin confirmed,
// not the draft they started from. Unticked rows are simply not there.

/** How an item reads to a coordinator. */
export const itemLabel = (assessmentName) => `Needs ${String(assessmentName || "").trim()}`;

const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// "Screening", "Screen", "SCR", "Scr 1" — but not "prescreen" mid-word.
const isScreeningLabel = (label) => /\bscr(?:een\w*)?\b/i.test(String(label || ""));

/**
 * Which visits are the screening visit(s)?
 *
 *   1. Every visit the extractor typed "screening".
 *   2. Failing that, any visit whose label says "screen" — an extractor that
 *      typed everything "unknown" usually still kept the header text.
 *   3. Failing that, the first column of the first table, flagged as a guess,
 *      so the admin is told to check it rather than handed nothing.
 *
 * @returns {{ visits: object[], guessed: boolean }}
 */
export const pickScreeningVisits = (extraction) => {
  const visits = Array.isArray(extraction?.visits) ? extraction.visits : [];
  const typed = visits.filter((v) => v && v.visit_type === "screening");
  if (typed.length) return { visits: typed, guessed: false };
  const labelled = visits.filter((v) => v && isScreeningLabel(v.visit_label));
  if (labelled.length) return { visits: labelled, guessed: false };
  if (!visits.length) return { visits: [], guessed: false };
  const first = [...visits].sort(
    (a, b) => (a.column_index ?? 0) - (b.column_index ?? 0) || (a.source_page ?? 0) - (b.source_page ?? 0)
  )[0];
  return { visits: [first], guessed: true };
};

/**
 * Build the draft checklist from an extraction.
 *
 * @param {object} extraction  the working or approved SoA extraction
 * @returns {{ items: object[], visitLabel: string, visitIds: string[], guessedVisit: boolean }}
 *   items are `{ id, label, assessmentName, source: "soa", footnotes: string[], selected: false }`
 */
export const deriveScreeningItems = (extraction) => {
  const { visits, guessed } = pickScreeningVisits(extraction);
  const visitIds = new Set(visits.map((v) => v.visit_id));
  const footMap = new Map((extraction?.footnotes || []).map((f) => [String(f.ref), String(f.text || "")]));

  const items = [];
  const seen = new Set();
  (extraction?.assessments || []).forEach((a, idx) => {
    if (!a || !String(a.name || "").trim()) return;
    const cell = (a.cells || []).find((c) => c && visitIds.has(c.visit_id) && c.marker !== "blank");
    if (!cell) return;
    const key = norm(a.name);
    if (!key || seen.has(key)) return;
    seen.add(key);
    const refs = new Set([...(a.footnote_refs || []), ...(cell.footnote_refs || [])].map(String));
    const footnotes = [...refs].map((r) => footMap.get(r)).filter(Boolean);
    items.push({
      id: `soa-${a.assessment_id || idx}`,
      label: itemLabel(a.name),
      assessmentName: String(a.name).trim(),
      source: "soa",
      footnotes,
      // Unticked by default: the checklist lists only what must be done AT
      // THE HOSPITAL, usually a short subset of the screening column, so the
      // admin ticks in rather than out. Every row is still listed.
      selected: false,
    });
  });

  return {
    items,
    visitLabel: visits.map((v) => v.visit_label).filter(Boolean).join(" / "),
    visitIds: [...visitIds],
    guessedVisit: guessed,
  };
};

/** A hand-added item. */
export const manualItem = (text) => {
  const t = String(text || "").trim();
  if (!t) return null;
  const label = /^needs\b/i.test(t) ? t : itemLabel(t);
  return {
    id: `manual-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    label,
    assessmentName: label.replace(/^needs\s+/i, ""),
    source: "manual",
    footnotes: [],
    selected: true,
  };
};

/**
 * What gets written to the study when the admin confirms. Only selected items,
 * only the fields the patient page needs — no `selected` flag, because "in the
 * list" is the flag.
 */
export const buildConfirmedScreeningVisit = ({ items, protocolId, visitLabel, visitIds, guessedVisit, confirmedBy }) => ({
  status: "confirmed",
  items: (items || [])
    .filter((i) => i && i.selected !== false && String(i.label || "").trim())
    .map((i) => ({
      id: i.id,
      label: String(i.label).trim(),
      assessmentName: String(i.assessmentName || i.label).trim(),
      source: i.source === "manual" ? "manual" : "soa",
      footnotes: Array.isArray(i.footnotes) ? i.footnotes.filter(Boolean) : [],
    })),
  protocolId: protocolId || "",
  visitLabel: visitLabel || "",
  visitIds: Array.isArray(visitIds) ? visitIds : [],
  guessedVisit: Boolean(guessedVisit),
  confirmedAt: new Date().toISOString(),
  confirmedBy: String(confirmedBy || "").toLowerCase(),
});

/**
 * Has an admin confirmed this study's checklist? An EMPTY confirmed list is a
 * real answer — "nothing needs to be done at the hospital" — and counts, so it
 * is shown as such rather than looking like a checklist nobody has done.
 */
export const hasConfirmedScreening = (study) => Boolean(study?.screeningVisit?.status === "confirmed");

/** A confirmed checklist with nothing on it: nothing needs doing at the hospital. */
export const isEmptyConfirmedScreening = (study) =>
  hasConfirmedScreening(study) && !(study.screeningVisit.items || []).length;

/**
 * Has the SAME protocol's schedule moved on since this checklist was confirmed?
 *
 * A confirmed checklist is a snapshot; nothing rebuilds it. That is the right
 * behaviour — it is a regulated list someone signed off — but it means an SoA
 * corrected in SourceX afterwards leaves the two quietly disagreeing, with the
 * checklist still asserting something the protocol no longer says. This is the
 * comparison behind the notice that tells an admin to look.
 *
 * Only call it when the extraction belongs to the protocol the checklist came
 * from. A DIFFERENT protocol is a separate situation with its own notice.
 *
 * Reports only UNAMBIGUOUS divergence:
 *
 *   removed  — on the checklist, no longer marked at screening in the SoA.
 *   renamed  — same SoA row, different name now. Compares `assessmentName`,
 *              which keeps the SoA's own wording even when an admin has
 *              relabelled the item (relabel touches `label` only), so an
 *              admin's own rewording never reads as a protocol change.
 *   visit    — the screening column itself is different.
 *
 * Deliberately NOT reported: rows newly marked at screening. Only SELECTED
 * items are stored, so a row the admin deliberately unticked is indistinguish-
 * able from one the edit added, and flagging those would fire on nearly every
 * checklist. Re-opening the checklist surfaces them unticked, which is where
 * that belongs.
 *
 * @returns {{changed: boolean, removed: string[], renamed: Array<{from: string, to: string}>, visitChanged: boolean, visitLabel: string}}
 */
export const diffConfirmedAgainstSoa = (confirmed, extraction) => {
  const none = { changed: false, removed: [], renamed: [], visitChanged: false, visitLabel: "" };
  if (!confirmed || !extraction) return none;

  const current = deriveScreeningItems(extraction);
  // An extraction that yields nothing is far more likely to be a read that has
  // not landed than a protocol that genuinely stopped requiring everything.
  // Claiming every item was removed on that basis would be alarming and wrong.
  if (!current.items.length) return none;

  const currentById = new Map(current.items.map((i) => [i.id, i]));
  const fromSoa = (confirmed.items || []).filter((i) => i && i.source !== "manual");

  const removed = [];
  const renamed = [];
  fromSoa.forEach((i) => {
    const now = currentById.get(i.id);
    if (!now) {
      removed.push(i.assessmentName || i.label || "");
      return;
    }
    if (norm(now.assessmentName) !== norm(i.assessmentName || i.label)) {
      renamed.push({ from: i.assessmentName || i.label || "", to: now.assessmentName });
    }
  });

  // A blank label on either side is missing information, not a change.
  const visitChanged =
    Boolean(confirmed.visitLabel) &&
    Boolean(current.visitLabel) &&
    norm(confirmed.visitLabel) !== norm(current.visitLabel);

  return {
    changed: Boolean(removed.length || renamed.length || visitChanged),
    removed: removed.filter(Boolean),
    renamed,
    visitChanged,
    visitLabel: current.visitLabel,
  };
};
